import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { parseMentions, renderWithMentions, insertMentionToken, removeMentionToken } from '@/lib/mentions'
import DraftMentionPills from '@/components/DraftMentionPills'
import LogRequestModal from '@/components/LogRequestModal'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatRelativeDate, formatDate, getInitials, getStatusColor, formatCurrency } from '@/lib/utils'
import {
  ArrowLeft, Send, Clock, CheckCircle2, Circle, ChevronDown,
  X, Check, AlertTriangle, Paperclip, Smile, Tag, User,
  Users, Star, Bell, Merge, MoreHorizontal, Mail, Phone,
  MessageSquare, Smartphone, Globe, Zap, Archive, Clock3,
  Plus, XCircle, Loader2, ClipboardList,
} from 'lucide-react'
import { toast } from 'sonner'
import Combobox from '@/components/Combobox'
import Avatar from '@/components/Avatar'
import InboxMessageAttachments from './InboxMessageAttachments'
import { uploadInboxAttachment, deleteInboxAttachment, INBOX_ATTACHMENT_ACCEPT_ATTR, MAX_INBOX_ATTACHMENT_BYTES, type InboxAttachment } from '@/lib/inboxAttachments'

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  email: <Mail style={{ width: 13, height: 13 }} />,
  chat: <MessageSquare style={{ width: 13, height: 13 }} />,
  phone: <Phone style={{ width: 13, height: 13 }} />,
  sms: <Smartphone style={{ width: 13, height: 13 }} />,
  portal: <Globe style={{ width: 13, height: 13 }} />,
}

const STATUS_OPTIONS = [
  { value: 'open',     label: 'Open',     color: 'var(--positive)',     icon: Circle },
  { value: 'pending',  label: 'Pending',  color: 'var(--warning)',      icon: Clock },
  { value: 'resolved', label: 'Resolved', color: 'var(--ink-tertiary)', icon: CheckCircle2 },
]

const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Low',    color: 'var(--ink-tertiary)' },
  { value: 'normal', label: 'Normal', color: 'var(--accent)' },
  { value: 'high',   label: 'High',   color: 'var(--warning)' },
  { value: 'urgent', label: 'Urgent', color: 'var(--danger)' },
]

const CANNED_SHORTCUTS = [
  { shortcut: '/hi', content: 'Hi! Thanks for reaching out. How can I help you today?' },
  { shortcut: '/thanks', content: 'Thank you for contacting us! Is there anything else I can help you with?' },
  { shortcut: '/follow', content: 'I wanted to follow up on our previous conversation. Have you had a chance to review our proposal?' },
  { shortcut: '/close', content: 'Thank you for your message. I\'m going to go ahead and close this conversation. Feel free to reach out if you need anything else!' },
]

type TagRow = { id: string; name: string; color: string }

// Light helper used by the chip renderer to soften the badge background from the
// stored hex color. Hex shorthand isn't supported (the DB default is '#7c3aed');
// fall back to the canonical purple when the value is unparsable.
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return `rgba(124,58,237,${alpha})`
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}

function TagSelector({
  workspaceTags,
  selectedIds,
  onAttach,
  onDetach,
  onCreate,
}: {
  workspaceTags: TagRow[]
  selectedIds: Set<string>
  onAttach: (tagId: string) => void
  onDetach: (tagId: string) => void
  onCreate: (name: string) => Promise<TagRow | null>
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const q = input.trim().toLowerCase()
  const filtered = q
    ? workspaceTags.filter(t => t.name.toLowerCase().includes(q))
    : workspaceTags
  const exactMatch = q ? workspaceTags.find(t => t.name.toLowerCase() === q) : null
  const canCreate = !!q && !exactMatch

  const handleCreate = async () => {
    if (!canCreate) return
    const created = await onCreate(q)
    if (created) {
      onAttach(created.id)
      setInput('')
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ink-secondary)', background: 'var(--hover-bg)', border: '1px dashed var(--border-strong)', borderRadius: 100, padding: '2px 9px', cursor: 'pointer' }}>
        <Plus style={{ width: 10, height: 10 }} /> Add tag
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, overflow: 'hidden', zIndex: 50, minWidth: 220, boxShadow: 'var(--popup-shadow)' }}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canCreate) { e.preventDefault(); handleCreate() } }}
            placeholder="Filter or create…" autoFocus
            style={{ width: '100%', padding: '8px 12px', background: 'var(--field-bg)', border: 'none', borderBottom: '1px solid var(--border-subtle)', outline: 'none', fontSize: 12, color: 'var(--ink-primary)', boxSizing: 'border-box' }} />
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {filtered.length === 0 && !canCreate && (
              <p style={{ margin: 0, padding: '10px 12px', fontSize: 12, color: 'var(--ink-tertiary)' }}>No tags yet</p>
            )}
            {filtered.map(t => {
              const checked = selectedIds.has(t.id)
              return (
                <button key={t.id} onClick={() => checked ? onDetach(t.id) : onAttach(t.id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  {/* t.color is DB user content — render swatch raw */}
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--ink-primary)', flex: 1 }}>{t.name}</span>
                  {checked && <Check style={{ width: 11, height: 11, color: 'var(--accent-text)' }} />}
                </button>
              )
            })}
            {canCreate && (
              <button onClick={handleCreate}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--accent-dim)', border: 'none', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--accent-dim)'}>
                <Plus style={{ width: 11, height: 11, color: 'var(--accent-text)' }} />
                <span style={{ fontSize: 12, color: 'var(--accent-text)' }}>Create "{q}"</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function ConversationDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, workspace } = useAuth()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [message, setMessage] = useState('')
  // ─── Composer attachment (single file, upload-on-pick; both note + reply) ────
  const [pendingAttachment, setPendingAttachment] = useState<InboxAttachment | null>(null)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const attachInputRef = useRef<HTMLInputElement>(null)

  const handlePickAttachment = async (file: File) => {
    if (!workspace?.id || !id) return
    if (file.size > MAX_INBOX_ATTACHMENT_BYTES) { toast.error('File is too large (max 15MB)'); return }
    setUploadingAttachment(true)
    const { data, error } = await uploadInboxAttachment(file, workspace.id, id)
    setUploadingAttachment(false)
    if (error || !data) { toast.error(error?.message || 'Upload failed'); return }
    setPendingAttachment(data)
  }

  const handleRemoveAttachment = () => {
    // Orphan cleanup: uploaded on pick but never referenced by a message.
    if (pendingAttachment) deleteInboxAttachment(pendingAttachment.path).catch(() => {})
    setPendingAttachment(null)
  }
  const [messageType, setMessageType] = useState<'outbound' | 'note'>('outbound')
  const [sending, setSending] = useState(false)
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const [showPriorityMenu, setShowPriorityMenu] = useState(false)
  const [showAssignMenu, setShowAssignMenu] = useState(false)
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showCanned, setShowCanned] = useState(false)
  const [cannedSearch, setCannedSearch] = useState('')
  const [rightPanel, setRightPanel] = useState<'details' | 'activity'>('details')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data: conversation, isLoading } = useQuery({
    queryKey: ['conversation', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('conversations').select('*').eq('id', id).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  // Tickets CP2: one conversation → at most one request. A single maybeSingle
  // on mount decides Create-vs-View (cheap; indexed enough for the volume).
  const [requestModalOpen, setRequestModalOpen] = useState(false)
  const { data: linkedRequest } = useQuery<{ id: string } | null>({
    queryKey: ['conversation-request', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('portal_requests').select('id').eq('conversation_id', id!).limit(1).maybeSingle()
      if (error) { console.error('[inbox] linked-request query failed', error); throw error }
      return data ?? null
    },
    enabled: !!id,
  })

  // Gap #3 CP2: link this conversation to a deal (writes conversations.deal_id;
  // the conversation row already carries deal_id via select('*')). Mirrors the
  // QuoteDetail "Link a deal" picker.
  const [changingDeal, setChangingDeal] = useState(false)
  const [dealPickerQuery, setDealPickerQuery] = useState('')
  const dealId: string | null = conversation?.deal_id ?? null

  const { data: linkedDeal } = useQuery({
    queryKey: ['conversation-linked-deal', dealId],
    queryFn: async () => {
      const { data } = await (supabase as any).from('deals').select('id,title,value,stage_name,status').eq('id', dealId).maybeSingle()
      return data
    },
    enabled: !!dealId,
  })

  // Candidate deals — all workspace deals, with the conversation's contact's deals
  // surfaced first (a conversation usually relates to its contact's deal), but any
  // deal is linkable.
  const { data: allDeals = [] } = useQuery({
    queryKey: ['conversation-deal-candidates', workspace?.id, conversation?.contact_id],
    queryFn: async () => {
      const { data } = await (supabase as any).from('deals').select('id,title,value,stage_name,status,contact_id').eq('workspace_id', workspace!.id).order('created_at', { ascending: false })
      const rows = (data || []) as any[]
      const cid = conversation?.contact_id
      if (!cid) return rows
      // contact's deals first, then the rest (stable within each group)
      return [...rows.filter(d => d.contact_id === cid), ...rows.filter(d => d.contact_id !== cid)]
    },
    enabled: !!workspace?.id,
  })

  const afterDealWrite = () => {
    setChangingDeal(false)
    queryClient.invalidateQueries({ queryKey: ['conversation', id] })            // refresh this card
    queryClient.invalidateQueries({ queryKey: ['conversation-linked-deal'] })
    queryClient.invalidateQueries({ queryKey: ['deal-conversations'] })          // DealDetail reflects it live
  }
  const linkDeal = useMutation({
    mutationFn: async (newDealId: string) => {
      const { error } = await (supabase as any).from('conversations').update({ deal_id: newDealId }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { afterDealWrite(); setDealPickerQuery(''); toast.success('Deal linked') },
    onError: () => toast.error('Failed to link deal'),
  })
  const unlinkDeal = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any).from('conversations').update({ deal_id: null }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { afterDealWrite(); toast.success('Deal unlinked') },
    onError: () => toast.error('Failed to unlink deal'),
  })

  const { data: messages = [] } = useQuery({
    queryKey: ['messages', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('messages').select('*').eq('conversation_id', id).order('created_at', { ascending: true })
      if (error) throw error
      return data || []
    },
    enabled: !!id,
    refetchInterval: 30000,   // slow safety fallback — realtime (below) is the live path
  })

  // ─── Realtime: open-conversation messages (Pattern A — patch-by-id) ───────
  // REPLICA IDENTITY FULL + select('*') means the payload carries the full row,
  // so INSERT appends (dedup by id) and UPDATE replaces in place (e.g. edited /
  // soft-deleted). Mirrors the team-chat messages sub. RLS scopes to the workspace.
  useEffect(() => {
    if (!id) return
    const channel = supabase
      .channel(`inbox-messages:${id}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'messages',
        filter: `conversation_id=eq.${id}`,
      }, (payload) => {
        const incoming = payload.new as any
        if (!incoming?.id) return
        queryClient.setQueryData<any[]>(['messages', id], (old) => {
          const arr = old || []
          if (arr.some(m => m.id === incoming.id)) return arr.map(m => m.id === incoming.id ? incoming : m)
          return [...arr, incoming]
        })
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [id, queryClient])

  const { data: agents = [] } = useQuery({
    queryKey: ['agents', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name, role').eq('workspace_id', workspace.id)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: cannedResponses = [] } = useQuery({
    queryKey: ['canned-responses', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return CANNED_SHORTCUTS
      const { data } = await supabase.from('canned_responses').select('*').eq('workspace_id', workspace.id)
      return data?.length ? data : CANNED_SHORTCUTS
    },
    enabled: !!workspace?.id,
  })

  const { data: workspaceTags = [] } = useQuery<TagRow[]>({
    queryKey: ['tags', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('tags').select('id, name, color').eq('workspace_id', workspace.id).order('name')
      return (data ?? []) as TagRow[]
    },
    enabled: !!workspace?.id,
  })

  // Normalized tags linked to THIS conversation. The supabase select-with-relation
  // form returns the joined `tags` row inline; we flatten to TagRow[].
  const { data: conversationTags = [] } = useQuery<TagRow[]>({
    queryKey: ['conversation-tags', id],
    queryFn: async () => {
      if (!id) return []
      const { data } = await supabase
        .from('conversation_tags')
        .select('tag:tags(id, name, color)')
        .eq('conversation_id', id)
      // supabase codegen types FK joins as arrays even when the relation is
      // one-to-one; the runtime shape here is `{ tag: TagRow | null }` per row.
      const rows = (data ?? []) as unknown as Array<{ tag: TagRow | null }>
      return rows.map(r => r.tag).filter((t): t is TagRow => !!t)
    },
    enabled: !!id,
  })

  const selectedTagIds = new Set(conversationTags.map(t => t.id))

  const attachTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      if (!id) throw new Error('No conversation')
      const { error } = await supabase.from('conversation_tags').insert({ conversation_id: id, tag_id: tagId })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-tags', id] })
      queryClient.invalidateQueries({ queryKey: ['conversation-tags-map'] })
    },
    onError: (e: any) => toast.error(e.message?.includes('duplicate') ? 'Tag already attached' : 'Failed to add tag'),
  })

  const detachTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      if (!id) throw new Error('No conversation')
      const { error } = await supabase.from('conversation_tags').delete().eq('conversation_id', id).eq('tag_id', tagId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-tags', id] })
      queryClient.invalidateQueries({ queryKey: ['conversation-tags-map'] })
    },
    onError: () => toast.error('Failed to remove tag'),
  })

  const createTag = async (name: string): Promise<TagRow | null> => {
    if (!workspace?.id) return null
    const { data, error } = await supabase
      .from('tags')
      .insert({ workspace_id: workspace.id, name })
      .select('id, name, color')
      .single()
    if (error) {
      toast.error(error.message.includes('duplicate') ? 'Tag already exists' : 'Failed to create tag')
      return null
    }
    queryClient.invalidateQueries({ queryKey: ['tags', workspace.id] })
    return data as TagRow
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('conversations').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', id] })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: () => toast.error('Failed to update'),
  })

  const sendMessage = async () => {
    if (!message.trim() || !id || !workspace?.id) return
    setSending(true)
    const body = message.trim()
    // Single-file v1. Attachments are stored + shown in the thread for BOTH modes
    // (note + reply) but are NOT emailed to the customer (internal-only; Resend
    // email-attachment delivery is a deferred slice — the edge fn is untouched).
    const atts = pendingAttachment ? [pendingAttachment] : []

    if (messageType === 'outbound') {
      // Route through the send-conversation-reply Edge Function — it owns the
      // From-address lookup, threading headers, Resend POST, messages-row insert
      // (with external_message_id/in_reply_to/references_ids), and conversation
      // last_message bump. Client just invokes + invalidates.
      const { data: sent, error } = await supabase.functions.invoke('send-conversation-reply', {
        body: { conversation_id: id, content: body },
      })
      if (error) {
        // supabase-js wraps non-2xx into a generic FunctionsHttpError; the real
        // error body lives on .context. Try to surface the function's actual
        // message (e.g. "No inbox email address configured for this workspace…").
        const ctx = (error as any).context
        const errBody = ctx?.json ? await ctx.json().catch(() => null) : null
        toast.error(errBody?.error || error.message || 'Failed to send')
        setSending(false)
        return
      }
      // Internal-only: attach the file to the just-created outbound row (the edge
      // fn returns its message_id). A client UPDATE — inherits messages
      // workspace_isolation RLS. The edge fn / email delivery is NOT modified;
      // the attachment is stored + shown in the thread, not emailed to the customer.
      if (atts.length && (sent as any)?.message_id) {
        await supabase.from('messages').update({ attachments: atts }).eq('id', (sent as any).message_id)
      }
    } else {
      // Internal note — local row insert, no email delivery.
      const { data: noteRow, error } = await supabase.from('messages').insert({
        conversation_id: id,
        workspace_id: workspace.id,
        content: body,
        type: 'note',
        sender_id: profile?.id,
        sender_name: profile?.full_name || 'Agent',
        sender_type: 'agent',
        is_read: true,
        attachments: atts,
      }).select('id').single()
      if (error) {
        toast.error('Failed to save note')
        setSending(false)
        return
      }
      await supabase.from('conversations').update({
        last_message: body,
        last_message_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', id)
      // parse-on-send (NOTE only): @[Name](id) tokens → note_mentions rows. The
      // trigger bells each mentioned teammate (skip-self DB-side). A failed insert
      // must not lose the note — log and move on. Outbound replies never reach here.
      const mentions = parseMentions(body)
      if (noteRow?.id && mentions.length && profile?.id) {
        const rows = mentions.map(mn => ({
          message_id: noteRow.id,
          conversation_id: id,
          workspace_id: workspace.id,
          mentioned_user_id: mn.userId,
          mentioning_user_id: profile.id,
        }))
        const { error: mErr } = await (supabase as any).from('note_mentions').insert(rows)
        if (mErr) console.warn('[note-mentions] insert failed', mErr.message)
      }
    }

    queryClient.invalidateQueries({ queryKey: ['messages', id] })
    queryClient.invalidateQueries({ queryKey: ['conversations'] })
    setMessage('')
    setPendingAttachment(null)
    setSending(false)
  }

  // ─── @-mention picker (NOTE MODE ONLY) — workspace members ────────────────
  const [showMention, setShowMention] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const mentionMatches = showMention
    ? (agents as any[])
        .map(a => ({ id: a.id as string, fullName: (a.full_name as string | null) || 'Unknown user' }))
        .filter(m => m.fullName.toLowerCase().includes(mentionQuery.toLowerCase()))
        .slice(0, 6)
    : []
  const selectMention = (mb: { id: string; fullName: string }) => {
    const ta = textareaRef.current
    const caret = ta?.selectionStart ?? message.length
    const { value, caret: newCaret } = insertMentionToken(message, caret, mb.fullName, mb.id)
    setMessage(value)
    setShowMention(false)
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(newCaret, newCaret) } })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Mention picker intercepts navigation/select BEFORE the ⌘+Enter send + '/' picker.
    if (showMention && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionMatches.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionMatches.length) % mentionMatches.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mentionMatches[mentionIndex]); return }
      if (e.key === 'Escape') { e.preventDefault(); setShowMention(false); return }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendMessage(); return }
    if (e.key === '/' && message === '') { setShowCanned(true); setCannedSearch(''); return }
    if (showCanned && e.key === 'Escape') { setShowCanned(false) }
  }

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setMessage(val)
    if (val.startsWith('/') && val.length > 1) { setShowCanned(true); setCannedSearch(val.slice(1)) }
    else if (!val.startsWith('/')) { setShowCanned(false) }
    // @-mention picker — NOTE MODE ONLY (never on a customer-facing reply).
    if (messageType === 'note') {
      const caret = e.target.selectionStart ?? val.length
      const m = val.slice(0, caret).match(/@(\w*)$/)
      if (m) { setShowMention(true); setMentionQuery(m[1]); setMentionIndex(0) }
      else setShowMention(false)
    } else {
      setShowMention(false)
    }
  }

  const insertCanned = (content: string) => { setMessage(content); setShowCanned(false); textareaRef.current?.focus() }

  const snoozeOptions = [
    { label: '1 hour', hours: 1 },
    { label: '4 hours', hours: 4 },
    { label: 'Tomorrow', hours: 24 },
    { label: '3 days', hours: 72 },
    { label: '1 week', hours: 168 },
  ]

  const filteredCanned = cannedResponses.filter((c: any) =>
    !cannedSearch || c.shortcut?.includes(cannedSearch) || c.content?.toLowerCase().includes(cannedSearch.toLowerCase()) || c.name?.toLowerCase().includes(cannedSearch.toLowerCase())
  )

  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (!conversation) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-secondary)', background: 'var(--canvas)', height: '100%' }}>Conversation not found</div>

  const statusOption = STATUS_OPTIONS.find(s => s.value === conversation.status) || STATUS_OPTIONS[0]
  const priorityOption = PRIORITY_OPTIONS.find(p => p.value === conversation.priority) || PRIORITY_OPTIONS[1]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Top bar — wraps on narrow viewports so the back/avatar/title row
          and the action buttons (status/priority/assign/snooze/resolve)
          drop to a second line on mobile instead of overflowing. */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, background: 'var(--surface-raised)', flexWrap: 'wrap' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/inbox') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} />
        </button>

        <Avatar name={conversation.contact_name || 'U'} size={32} />

        <div style={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {conversation.contact_name}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 100, background: 'var(--hover-bg)', flexShrink: 0 }}>
              <span style={{ color: 'var(--ink-secondary)' }}>{CHANNEL_ICONS[conversation.channel]}</span>
              <span style={{ fontSize: 10, color: 'var(--ink-tertiary)', textTransform: 'capitalize' }}>{conversation.channel}</span>
            </div>
          </div>
          {conversation.subject && (
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {conversation.subject}
            </p>
          )}
        </div>

        {/* Action buttons — also allow internal wrap so a long set of
            dropdowns/badges can flow across multiple lines on mobile. */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Create/View request (Tickets CP2) — one conversation, one request. */}
          <button
            onClick={() => { if (linkedRequest) navigate('/app/requests'); else setRequestModalOpen(true) }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)' }}>
            <ClipboardList style={{ width: 12, height: 12 }} />
            {linkedRequest ? 'View request' : 'Create request'}
          </button>

          {/* Status */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowStatusMenu(!showStatusMenu); setShowPriorityMenu(false); setShowAssignMenu(false) }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: statusOption.color }}>
              <statusOption.icon style={{ width: 12, height: 12 }} />
              {statusOption.label}
              <ChevronDown style={{ width: 10, height: 10, opacity: 0.5 }} />
            </button>
            <AnimatePresence>
              {showStatusMenu && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                  style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 140 }}>
                  {STATUS_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => { updateMutation.mutate({ status: opt.value }); setShowStatusMenu(false); toast.success(`Marked ${opt.label}`) }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: opt.color, textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <opt.icon style={{ width: 13, height: 13 }} />
                      {opt.label}
                      {conversation.status === opt.value && <Check style={{ width: 11, height: 11, marginLeft: 'auto' }} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Priority */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowPriorityMenu(!showPriorityMenu); setShowStatusMenu(false); setShowAssignMenu(false) }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: priorityOption.color }}>
              <AlertTriangle style={{ width: 12, height: 12 }} />
              {priorityOption.label}
              <ChevronDown style={{ width: 10, height: 10, opacity: 0.5 }} />
            </button>
            <AnimatePresence>
              {showPriorityMenu && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                  style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 140 }}>
                  {PRIORITY_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => { updateMutation.mutate({ priority: opt.value }); setShowPriorityMenu(false) }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: opt.color, textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      {opt.label}
                      {conversation.priority === opt.value && <Check style={{ width: 11, height: 11 }} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Assign */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowAssignMenu(!showAssignMenu); setShowStatusMenu(false); setShowPriorityMenu(false) }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: conversation.assigned_name ? 'var(--accent-text)' : 'var(--ink-secondary)' }}>
              <User style={{ width: 12, height: 12 }} />
              {conversation.assigned_name ? conversation.assigned_name.split(' ')[0] : 'Assign'}
              <ChevronDown style={{ width: 10, height: 10, opacity: 0.5 }} />
            </button>
            <AnimatePresence>
              {showAssignMenu && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                  style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 160 }}>
                  <button onClick={() => { updateMutation.mutate({ assigned_to: null, assigned_name: null }); setShowAssignMenu(false); toast.success('Unassigned') }}
                    style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-tertiary)', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    Unassign
                  </button>
                  {agents.map((agent: any) => (
                    <button key={agent.id} onClick={() => { updateMutation.mutate({ assigned_to: agent.id, assigned_name: agent.full_name }); setShowAssignMenu(false); toast.success(`Assigned to ${agent.full_name}`) }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-secondary)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      {/* Fixed-purple agent badge — non-theming chrome (operator pattern: agent = always purple) */}
                      <div style={{ width: 22, height: 22, borderRadius: 7, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent-ink)' }}>
                        {getInitials(agent.full_name)}
                      </div>
                      {agent.full_name}
                      {conversation.assigned_to === agent.id && <Check style={{ width: 11, height: 11, marginLeft: 'auto', color: 'var(--accent-text)' }} />}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Snooze */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowSnoozeMenu(!showSnoozeMenu); setShowMoreMenu(false) }}
              title="Snooze"
              style={{ width: 32, height: 32, borderRadius: 8, background: conversation.is_snoozed ? 'color-mix(in srgb, var(--info) var(--status-tint-fill), transparent)' : 'var(--field-bg)', border: conversation.is_snoozed ? '1px solid var(--info-border)' : '1px solid var(--border-default)', cursor: 'pointer', color: conversation.is_snoozed ? 'var(--info)' : 'var(--ink-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock3 style={{ width: 14, height: 14 }} />
            </button>
            <AnimatePresence>
              {showSnoozeMenu && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
                  style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, minWidth: 140 }}>
                  {conversation.is_snoozed && (
                    <button onClick={() => { updateMutation.mutate({ is_snoozed: false, snoozed_until: null }); setShowSnoozeMenu(false); toast.success('Unsnoozed') }}
                      style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--danger)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      Wake up now
                    </button>
                  )}
                  {snoozeOptions.map(opt => (
                    <button key={opt.label} onClick={() => { updateMutation.mutate({ is_snoozed: true, snoozed_until: new Date(Date.now() + opt.hours * 60 * 60 * 1000).toISOString() }); setShowSnoozeMenu(false); toast.success(`Snoozed for ${opt.label}`) }}
                      style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-secondary)', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      {opt.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Resolve shortcut */}
          {conversation.status !== 'resolved' && (
            <button onClick={() => { updateMutation.mutate({ status: 'resolved' }); toast.success('Conversation resolved') }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <CheckCircle2 style={{ width: 13, height: 13 }} /> Resolve
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* Messages */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Tags bar — backed by normalized tags + conversation_tags.
              hexToRgba preserves DB user-content tag colors with runtime alpha — banked. */}
          <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tag style={{ width: 12, height: 12, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
            {conversationTags.map(t => (
              <span key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: hexToRgba(t.color, 0.15), color: t.color, border: `1px solid ${hexToRgba(t.color, 0.25)}` }}>
                {t.name}
                <button onClick={() => detachTagMutation.mutate(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.color, padding: 0, display: 'flex', lineHeight: 1 }}>
                  <X style={{ width: 9, height: 9 }} />
                </button>
              </span>
            ))}
            <TagSelector
              workspaceTags={workspaceTags}
              selectedIds={selectedTagIds}
              onAttach={id => attachTagMutation.mutate(id)}
              onDetach={id => detachTagMutation.mutate(id)}
              onCreate={createTag}
            />
          </div>

          {/* Message thread */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {messages.length === 0 ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Send style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                </div>
                <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No messages yet — start the conversation</p>
              </div>
            ) : (
              messages.map((msg: any, i: number) => {
                const isOutbound = msg.type === 'outbound'
                const isNote = msg.type === 'note'
                const isSystem = msg.type === 'system'
                const showAvatar = i === 0 || messages[i - 1]?.sender_type !== msg.sender_type || messages[i - 1]?.type !== msg.type

                if (isSystem) return (
                  <div key={msg.id} style={{ textAlign: 'center', padding: '8px 0' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', background: 'var(--hover-bg)', padding: '3px 12px', borderRadius: 100 }}>{msg.content}</span>
                  </div>
                )

                return (
                  <motion.div key={msg.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    style={{ display: 'flex', gap: 8, alignItems: 'flex-end', justifyContent: isOutbound ? 'flex-end' : 'flex-start', marginBottom: 2 }}>
                    {!isOutbound && showAvatar && (
                      <Avatar name={msg.sender_name || 'U'} size={28} />
                    )}
                    {!isOutbound && !showAvatar && <div style={{ width: 28, flexShrink: 0 }} />}

                    <div style={{ maxWidth: '70%' }}>
                      {showAvatar && (
                        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: `0 0 4px ${isOutbound ? 0 : 4}px`, textAlign: isOutbound ? 'right' : 'left' }}>
                          {msg.sender_name}
                        </p>
                      )}
                      <div style={{
                        padding: '10px 14px',
                        borderRadius: isOutbound ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                        background: isNote ? 'color-mix(in srgb, var(--warning) var(--status-tint-faint), transparent)' : isOutbound ? 'var(--logo-bg-gradient)' : 'var(--surface)',
                        border: isNote ? '1px solid var(--warning-border)' : isOutbound ? 'none' : '1px solid var(--border-subtle)',
                      }}>
                        {isNote && <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--warning)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Internal note</p>}
                        <p style={{ fontSize: 13, color: isOutbound ? 'var(--accent-ink)' : 'var(--ink-primary)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{renderWithMentions(msg.content)}</p>
                      </div>
                      {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                        <InboxMessageAttachments attachments={msg.attachments as InboxAttachment[]} />
                      )}
                      <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: '3px 4px 0', textAlign: isOutbound ? 'right' : 'left' }}>
                        {formatRelativeDate(msg.created_at)}
                      </p>
                    </div>

                    {isOutbound && showAvatar && (
                      /* Fixed-purple outbound agent avatar — non-theming chrome (operator pattern: agent = always purple) */
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--accent-ink)', flexShrink: 0 }}>
                        {getInitials(msg.sender_name || 'Agent')}
                      </div>
                    )}
                    {isOutbound && !showAvatar && <div style={{ width: 28, flexShrink: 0 }} />}
                  </motion.div>
                )
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply box */}
          <div style={{ padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0, background: 'var(--surface-raised)', position: 'relative' }}>

            {/* Canned responses picker */}
            <AnimatePresence>
              {showCanned && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  style={{ position: 'absolute', bottom: '100%', left: 20, right: 20, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 14, overflow: 'hidden', zIndex: 50, marginBottom: 8, boxShadow: 'var(--popup-shadow)' }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Zap style={{ width: 13, height: 13, color: 'var(--accent)' }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)' }}>Canned responses</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginLeft: 'auto' }}>Type to filter · Enter to insert</span>
                  </div>
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {filteredCanned.length === 0 ? (
                      <p style={{ padding: '12px 14px', fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>No matches</p>
                    ) : filteredCanned.map((c: any, i: number) => (
                      <button key={i} onClick={() => insertCanned(c.content)}
                        style={{ width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          {c.shortcut && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-dim)', padding: '1px 6px', borderRadius: 5, fontFamily: 'monospace' }}>{c.shortcut}</span>}
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)' }}>{c.name || c.shortcut}</span>
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.content}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* @-mention picker (note mode only) — workspace members */}
            <AnimatePresence>
              {showMention && mentionMatches.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  style={{ position: 'absolute', bottom: '100%', left: 20, right: 20, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 14, overflow: 'hidden', zIndex: 50, marginBottom: 8, boxShadow: 'var(--popup-shadow)' }}>
                  <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)' }}>Mention a teammate · ↑↓ then Enter</div>
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {mentionMatches.map((mb, i) => (
                      <button key={mb.id} type="button" onMouseDown={e => { e.preventDefault(); selectMention(mb) }}
                        onMouseEnter={() => setMentionIndex(i)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: i === mentionIndex ? 'var(--accent-dim)' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                        <Avatar name={mb.fullName} size={24} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{mb.fullName}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Type toggle */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[
                { type: 'outbound' as const, label: 'Reply',         color: 'var(--accent)' },
                { type: 'note' as const,     label: 'Internal note', color: 'var(--warning)' },
              ].map(opt => (
                <button key={opt.type} onClick={() => setMessageType(opt.type)}
                  style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: messageType === opt.type ? `color-mix(in srgb, ${opt.color} var(--status-tint-dim), transparent)` : 'transparent', color: messageType === opt.type ? opt.color : 'var(--ink-tertiary)', border: messageType === opt.type ? `1px solid color-mix(in srgb, ${opt.color} var(--status-tint-strong), transparent)` : '1px solid transparent', transition: 'all 150ms ease' }}>
                  {opt.label}
                </button>
              ))}
              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginLeft: 4, alignSelf: 'center' }}>
                Type / for canned responses
              </span>
            </div>

            {/* Textarea */}
            <div style={{ background: 'var(--field-bg)', border: `1px solid ${messageType === 'note' ? 'var(--warning-border)' : 'var(--border-default)'}`, borderRadius: 12, overflow: 'hidden' }}>
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                placeholder={messageType === 'note' ? 'Add an internal note... (⌘+Enter to save)' : 'Write a reply... (⌘+Enter to send) · Type / for canned responses'}
                rows={3}
                style={{ width: '100%', padding: '12px 14px', background: 'transparent', border: 'none', fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'none', lineHeight: 1.5, boxSizing: 'border-box' }}
              />
              {messageType === 'note' && (
                <DraftMentionPills content={message} onRemove={uid => setMessage(removeMentionToken(message, uid))} style={{ padding: '0 14px 10px' }} />
              )}
              {(pendingAttachment || uploadingAttachment) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 14px 10px', padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 10, maxWidth: 320 }}>
                  {uploadingAttachment ? (
                    <><Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)' }} className="animate-spin" /><span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Uploading…</span></>
                  ) : pendingAttachment && (
                    <>
                      <Paperclip style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)' }}>{pendingAttachment.name}</span>
                      <button type="button" onClick={handleRemoveAttachment} aria-label="Remove attachment" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 2, display: 'flex', flexShrink: 0 }}>
                        <X style={{ width: 14, height: 14 }} />
                      </button>
                    </>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input ref={attachInputRef} type="file" accept={INBOX_ATTACHMENT_ACCEPT_ATTR}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handlePickAttachment(f); e.target.value = '' }}
                    style={{ display: 'none' }} />
                  <button type="button"
                    onClick={() => attachInputRef.current?.click()}
                    disabled={uploadingAttachment || !!pendingAttachment}
                    style={{ background: 'none', border: 'none', cursor: (uploadingAttachment || pendingAttachment) ? 'not-allowed' : 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', opacity: (uploadingAttachment || pendingAttachment) ? 0.4 : 1 }}
                    title={pendingAttachment ? 'Remove the current attachment first' : 'Attach file'}
                    onMouseEnter={e => { if (!uploadingAttachment && !pendingAttachment) e.currentTarget.style.color = 'var(--ink-secondary)' }}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                    <Paperclip style={{ width: 15, height: 15 }} />
                  </button>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex' }}
                    title="Emoji"
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                    <Smile style={{ width: 15, height: 15 }} />
                  </button>
                  <button onClick={() => { setShowCanned(true); setCannedSearch('') }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex' }}
                    title="Canned responses"
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                    <Zap style={{ width: 15, height: 15 }} />
                  </button>
                </div>
                <button onClick={sendMessage} disabled={sending || uploadingAttachment || !message.trim()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', background: message.trim() ? (messageType === 'note' ? 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)' : 'var(--logo-bg-gradient)') : 'var(--hover-bg)', border: messageType === 'note' ? '1px solid var(--warning-border)' : 'none', borderRadius: 9, color: message.trim() ? (messageType === 'note' ? 'var(--warning)' : 'var(--accent-ink)') : 'var(--ink-tertiary)', fontSize: 13, fontWeight: 600, cursor: message.trim() ? 'pointer' : 'not-allowed', transition: 'all 150ms ease' }}>
                  <Send style={{ width: 13, height: 13 }} />
                  {sending ? 'Sending...' : messageType === 'note' ? 'Add note' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel — desktop only. On mobile (<768px) the message area
            takes the full viewport; status/priority/assignee are already
            reachable from the top-bar dropdowns. SLA/CSAT/timeline live
            here only and are hidden on mobile this pass (re-add as a
            drawer in a follow-up if needed). */}
        {!isMobile && (
        <div style={{ width: 260, borderLeft: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden' }}>

          {/* Panel tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            {[{ id: 'details', label: 'Details' }, { id: 'activity', label: 'Activity' }].map(tab => (
              <button key={tab.id} onClick={() => setRightPanel(tab.id as any)}
                style={{ flex: 1, padding: '11px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: rightPanel === tab.id ? 600 : 400, color: rightPanel === tab.id ? 'var(--ink-primary)' : 'var(--ink-tertiary)', borderBottom: rightPanel === tab.id ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all 150ms ease' }}>
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '16px 16px' }}>
            {rightPanel === 'details' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Contact */}
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Contact</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    onClick={() => conversation.contact_id && navigate(`/app/contacts/${conversation.contact_id}`)}>
                    <Avatar name={conversation.contact_name || 'U'} size={30} />
                    <div style={{ overflow: 'hidden' }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conversation.contact_name}</p>
                      {conversation.contact_email && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conversation.contact_email}</p>}
                    </div>
                  </div>
                </div>

                {/* Linked deal — Gap #3 CP2: pin this thread to a deal (writes conversations.deal_id). */}
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Deal</p>
                  {dealId && !changingDeal ? (
                    linkedDeal ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
                        <button type="button" onClick={() => navigate(`/app/deals/${(linkedDeal as any).id}`)}
                          style={{ display: 'flex', flexDirection: 'column', minWidth: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(linkedDeal as any).title}</span>
                          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', whiteSpace: 'nowrap' }}>{formatCurrency((linkedDeal as any).value || 0)} · {(linkedDeal as any).stage_name ?? '—'}</span>
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <button type="button" onClick={() => setChangingDeal(true)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 11, fontWeight: 600, padding: 0 }}>Change</button>
                          <button type="button" aria-label="Unlink deal" onClick={() => unlinkDeal.mutate()}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center' }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--danger)' }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                            <XCircle style={{ width: 14, height: 14 }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Loading…</p>
                    )
                  ) : (
                    <Combobox
                      value={null}
                      inputValue={dealPickerQuery}
                      onInputChange={setDealPickerQuery}
                      onChange={(d: any) => { if (d) linkDeal.mutate(d.id) }}
                      options={allDeals as any[]}
                      getKey={(d: any) => d.id}
                      getLabel={(d: any) => d.title}
                      renderOption={(d: any) => <span style={{ fontSize: 12 }}>{d.title} · {formatCurrency(d.value || 0)} · {d.stage_name ?? '—'}</span>}
                      placeholder="Link a deal…"
                      emptyMessage="No deals"
                    />
                  )}
                </div>

                {/* Conversation info */}
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Conversation</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { label: 'Status', value: conversation.status, color: getStatusColor(conversation.status).text },
                      { label: 'Priority', value: conversation.priority, color: PRIORITY_OPTIONS.find(p => p.value === conversation.priority)?.color || 'var(--ink-primary)' },
                      { label: 'Channel', value: conversation.channel, color: 'var(--ink-secondary)' },
                      { label: 'Created', value: formatDate(conversation.created_at, 'MMM d, yyyy'), color: 'var(--ink-secondary)' },
                      { label: 'Updated', value: formatRelativeDate(conversation.updated_at), color: 'var(--ink-secondary)' },
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{item.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: item.color, textTransform: 'capitalize' }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SLA */}
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>SLA</p>
                  <div style={{ padding: '10px 12px', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
                    <SLADetail createdAt={conversation.created_at} status={conversation.status} />
                  </div>
                </div>

                {/* Assignee */}
                <div>
                  <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Assigned to</p>
                  {conversation.assigned_name ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--accent-dim)', borderRadius: 10, border: '1px solid var(--accent-border)' }}>
                      {/* Fixed-purple agent badge — non-theming chrome */}
                      <div style={{ width: 24, height: 24, borderRadius: 7, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--accent-ink)' }}>
                        {getInitials(conversation.assigned_name)}
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-text)' }}>{conversation.assigned_name}</span>
                    </div>
                  ) : (
                    <div style={{ padding: '8px 12px', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Unassigned</span>
                    </div>
                  )}
                </div>

                {/* CSAT */}
                {conversation.csat_score && (
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>CSAT Score</p>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} style={{ width: 16, height: 16, color: n <= conversation.csat_score ? 'var(--warning)' : 'var(--border-default)', fill: n <= conversation.csat_score ? 'var(--warning)' : 'none' }} />
                      ))}
                      <span style={{ fontSize: 12, color: 'var(--ink-secondary)', marginLeft: 6 }}>{conversation.csat_score}/5</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {rightPanel === 'activity' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Timeline</p>
                {messages.filter((m: any) => m.type === 'system' || m.type === 'note').length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '20px 0' }}>No activity yet</p>
                ) : (
                  messages.filter((m: any) => m.type === 'system' || m.type === 'note').map((msg: any) => (
                    <div key={msg.id} style={{ display: 'flex', gap: 8, padding: '8px 10px', background: msg.type === 'note' ? 'color-mix(in srgb, var(--warning) var(--status-tint-faint), transparent)' : 'var(--surface)', borderRadius: 9, border: msg.type === 'note' ? '1px solid var(--warning-border)' : '1px solid var(--border-subtle)' }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: msg.type === 'note' ? 'var(--warning)' : 'var(--accent)', flexShrink: 0, marginTop: 4 }} />
                      <div>
                        <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: '0 0 3px', lineHeight: 1.4 }}>{msg.content}</p>
                        <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>{msg.sender_name} · {formatRelativeDate(msg.created_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      <LogRequestModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onSaved={() => { setRequestModalOpen(false); queryClient.invalidateQueries({ queryKey: ['conversation-request', id] }) }}
        workspaceId={workspace?.id ?? null}
        prefill={{
          contactId: conversation?.contact_id ?? undefined,   // thread's linked contact, if any
          subject: conversation?.subject ?? undefined,
          conversationId: id,
          source: 'email',
        }}
      />
    </div>
  )
}

function SLADetail({ createdAt, status }: { createdAt: string; status: string }) {
  const created = new Date(createdAt)
  const now = new Date()
  const hoursOld = (now.getTime() - created.getTime()) / (1000 * 60 * 60)
  const slaHours = 24
  const remaining = slaHours - hoursOld
  const pct = Math.max(0, Math.min(100, (remaining / slaHours) * 100))
  const color = remaining < 2 ? 'var(--danger)' : remaining < 6 ? 'var(--warning)' : 'var(--positive)'
  const breached = hoursOld > slaHours

  if (status === 'resolved') return <p style={{ fontSize: 12, color: 'var(--positive)', margin: 0, fontWeight: 600 }}>✓ Resolved within SLA</p>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--ink-secondary)' }}>First response SLA</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: breached ? 'var(--danger)' : color }}>
          {breached ? 'Breached' : remaining < 1 ? `${Math.round(remaining * 60)}m left` : `${Math.round(remaining)}h left`}
        </span>
      </div>
      <div style={{ height: 4, background: 'var(--hover-bg)', borderRadius: 100, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 100, transition: 'width 300ms ease' }} />
      </div>
    </div>
  )
}